import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import axios from 'axios';
import { 
  CheckCircle, XCircle, Loader2, Clock, ArrowLeft,
  IndianRupee, AlertTriangle
} from 'lucide-react';

import { BACKEND_URL, API } from '../../utils/apiConfig';
import { trackConversion } from '../../utils/analytics';

export default function PaymentStatusPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('order_id');
  
  const [status, setStatus] = useState('loading');
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [error, setError] = useState('');
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    if (orderId) {
      verifyPayment();
    } else {
      setStatus('error');
      setError('No order ID found');
    }
  }, [orderId]);

  const verifyPayment = async () => {
    setRetrying(true);
    try {
      const response = await axios.post(`${API}/payments/verify`, {
        order_id: orderId
      });
      
      if (response.data.verified) {
        setStatus('success');
        setPaymentInfo(response.data);
        trackConversion('booking');
        trackConversion('payment', response.data.amount || 0);
      } else {
        setStatus('pending');
        setError(response.data.error || 'Payment verification pending');
      }
    } catch (err) {
      setStatus('error');
      setError(err.response?.data?.detail || 'Failed to verify payment');
    } finally {
      setRetrying(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-pink-50 flex items-center justify-center p-4">
      <div className="w-full max-w-sm text-center">
        {status === 'loading' && (
          <>
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-violet-100 flex items-center justify-center">
              <Loader2 className="w-10 h-10 text-violet-500 animate-spin" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Verifying Payment</h2>
            <p className="text-slate-500">Please wait while we confirm your payment...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="relative w-24 h-24 mx-auto mb-6">
              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 animate-ping opacity-20" />
              <div className="relative w-24 h-24 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center shadow-xl">
                <CheckCircle className="w-12 h-12 text-white" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-emerald-600 mb-2">Payment Successful!</h2>
            <p className="text-slate-500 mb-6">Your booking has been confirmed and paid.</p>
            
            {paymentInfo?.booking_id && (
              <div className="bg-white rounded-xl p-4 border border-emerald-200 mb-6">
                <p className="text-xs text-slate-500">Booking ID</p>
                <p className="font-mono font-bold text-lg text-violet-600">{paymentInfo.booking_id}</p>
              </div>
            )}
            
            <div className="space-y-3">
              <Button
                onClick={() => navigate('/my-bookings')}
                className="w-full h-12 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-semibold rounded-xl"
                data-testid="view-bookings-after-payment"
              >
                View My Bookings
              </Button>
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                className="w-full h-12 rounded-xl"
              >
                Back to Home
              </Button>
            </div>
          </>
        )}

        {status === 'pending' && (
          <>
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-amber-100 flex items-center justify-center">
              <Clock className="w-10 h-10 text-amber-500" />
            </div>
            <h2 className="text-xl font-bold text-amber-600 mb-2">Payment Pending</h2>
            <p className="text-slate-500 mb-6">Your payment is being processed. This may take a moment.</p>
            
            <div className="space-y-3">
              <Button
                onClick={verifyPayment}
                disabled={retrying}
                className="w-full h-12 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-xl"
                data-testid="retry-verification-btn"
              >
                {retrying ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
                Check Again
              </Button>
              <Button
                onClick={() => navigate('/my-bookings')}
                variant="outline"
                className="w-full h-12 rounded-xl"
              >
                View My Bookings
              </Button>
            </div>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center">
              <AlertTriangle className="w-10 h-10 text-red-500" />
            </div>
            <h2 className="text-xl font-bold text-red-600 mb-2">Payment Issue</h2>
            <p className="text-slate-500 mb-2">{error}</p>
            <p className="text-xs text-slate-400 mb-6">Your booking is still confirmed. You can pay at the venue.</p>
            
            <div className="space-y-3">
              <Button
                onClick={verifyPayment}
                disabled={retrying}
                variant="outline"
                className="w-full h-12 rounded-xl"
                data-testid="retry-payment-btn"
              >
                {retrying ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : null}
                Try Again
              </Button>
              <Button
                onClick={() => navigate('/my-bookings')}
                className="w-full h-12 bg-violet-500 hover:bg-violet-600 text-white font-semibold rounded-xl"
              >
                View My Bookings
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
